import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './BFFilterSelect.css';

// ============ TYPES ============

export interface BFFilterSelectOption<V = string> {
    /** Unique value identifier */
    value: V;
    /** Display content – can be plain text or any JSX (icons, badges, …) */
    label: React.ReactNode;
    /** When true the option is shown but cannot be interacted with */
    disabled?: boolean;
}

export type BFFilterSelectSize = 'sm' | 'md' | 'lg';

export interface BFFilterSelectProps<V = string> {
    /** Available options */
    options: BFFilterSelectOption<V>[];
    /**
     * Currently selected value(s).
     * Pass a single value for single-select mode or an array for multi-select mode.
     */
    value?: V | V[];
    /**
     * Fires when the selection changes.
     * – Single-select: receives `V | undefined`
     * – Multi-select:  receives `V[]`
     */
    onChange?: (value: V | V[] | undefined) => void;
    /** Allow selecting more than one option at a time */
    multiSelect?: boolean;
    /** Placeholder shown in the trigger when nothing is selected */
    placeholder?: string;
    /** Show a "Clear" action when at least one value is active */
    clearable?: boolean;
    /** Whether the whole control is disabled */
    disabled?: boolean;
    /** Size variant */
    size?: BFFilterSelectSize;
    /** Additional CSS class for the root element */
    className?: string;
}

// ============ INTERNAL ICONS ============

function ChevronIcon() {
    return (
        <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <polyline points="6 9 12 15 18 9" />
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function XIcon({ size = 10 }: { size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

// ============ COMPONENT ============

export function BFFilterSelect<V = string>({
    options,
    value,
    onChange,
    multiSelect = false,
    placeholder = 'All',
    clearable = true,
    disabled = false,
    size = 'md',
    className = '',
}: BFFilterSelectProps<V>) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Normalise selection to a flat array for uniform internal handling
    const selectedValues: V[] = useMemo(() => {
        if (value == null) return [];
        if (Array.isArray(value)) return value;
        return [value];
    }, [value]);
    const hasSelection = selectedValues.length > 0;

    // Close when clicking outside
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    // Keyboard: close on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        if (isOpen) document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen]);

    const toggleOpen = useCallback(() => {
        if (!disabled) setIsOpen((prev) => !prev);
    }, [disabled]);

    const isSelected = useCallback(
        (v: V) => selectedValues.some((sv) => sv === v),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedValues],
    );

    const handleSelect = useCallback(
        (opt: BFFilterSelectOption<V>) => {
            if (!onChange || opt.disabled) return;

            if (multiSelect) {
                const next = isSelected(opt.value)
                    ? selectedValues.filter((sv) => sv !== opt.value)
                    : [...selectedValues, opt.value];
                onChange(next);
            } else {
                // Single-select: clicking the active item deselects it
                onChange(isSelected(opt.value) ? undefined : opt.value);
                setIsOpen(false);
            }
        },
        [multiSelect, onChange, isSelected, selectedValues],
    );

    const handleClear = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (onChange) onChange(multiSelect ? ([] as unknown as V[]) : undefined);
        },
        [onChange, multiSelect],
    );

    // ---- trigger classes ----
    const triggerClasses = [
        'bf-filter-select__trigger',
        `bf-filter-select__trigger--${size}`,
        isOpen ? 'bf-filter-select__trigger--open' : '',
        hasSelection ? 'bf-filter-select__trigger--active' : '',
        disabled ? 'bf-filter-select__trigger--disabled' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div ref={containerRef} className={`bf-filter-select ${className}`.trim()}>
            {/* ---- Trigger button ---- */}
            <button
                type="button"
                className={triggerClasses}
                onClick={toggleOpen}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                disabled={disabled}
            >
                <span className="bf-filter-select__label">{placeholder}</span>

                {hasSelection && (
                    <span className="bf-filter-select__badge" aria-label={`${selectedValues.length} selected`}>
                        {selectedValues.length}
                    </span>
                )}

                {hasSelection && clearable ? (
                    <span
                        className="bf-filter-select__clear-inline"
                        role="button"
                        aria-label="Clear selection"
                        tabIndex={0}
                        onClick={handleClear}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleClear(e as unknown as React.MouseEvent);
                            }
                        }}
                    >
                        <XIcon />
                    </span>
                ) : (
                    <span
                        className={['bf-filter-select__chevron', isOpen ? 'bf-filter-select__chevron--open' : '']
                            .filter(Boolean)
                            .join(' ')}
                    >
                        <ChevronIcon />
                    </span>
                )}
            </button>

            {/* ---- Dropdown panel ---- */}
            {isOpen && (
                <div className="bf-filter-select__dropdown" role="listbox" aria-multiselectable={multiSelect}>
                    <ul className="bf-filter-select__list">
                        {options.map((opt, idx) => {
                            const selected = isSelected(opt.value);
                            const optClasses = [
                                'bf-filter-select__option',
                                selected ? 'bf-filter-select__option--selected' : '',
                                opt.disabled ? 'bf-filter-select__option--disabled' : '',
                            ]
                                .filter(Boolean)
                                .join(' ');

                            return (
                                <li
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={idx}
                                    className={optClasses}
                                    role="option"
                                    aria-selected={selected}
                                    aria-disabled={opt.disabled}
                                    onClick={() => handleSelect(opt)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSelect(opt);
                                        }
                                    }}
                                    tabIndex={opt.disabled ? -1 : 0}
                                >
                                    {/* Checkbox (multi) or radio (single) indicator */}
                                    <span
                                        className={[
                                            'bf-filter-select__indicator',
                                            multiSelect
                                                ? 'bf-filter-select__indicator--checkbox'
                                                : 'bf-filter-select__indicator--radio',
                                            selected ? 'bf-filter-select__indicator--checked' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        aria-hidden="true"
                                    >
                                        {selected &&
                                            (multiSelect ? (
                                                <CheckIcon />
                                            ) : (
                                                <span className="bf-filter-select__radio-dot" />
                                            ))}
                                    </span>

                                    <span className="bf-filter-select__option-label">{opt.label}</span>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Footer: Clear */}
                    {clearable && hasSelection && (
                        <div className="bf-filter-select__footer">
                            <button
                                type="button"
                                className="bf-filter-select__footer-btn bf-filter-select__footer-btn--clear"
                                onClick={handleClear}
                            >
                                Clear
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default BFFilterSelect;
